import ImageWithFallback from '@/components/ui/ImageWithFallback';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { ProductInnerGridItem } from '@/types/product';

interface ProductPpcApplicationsProps {
  heading?: string;
  subHeading?: string;
  gridItems?: ProductInnerGridItem[];
}

const defaultApplications: ProductInnerGridItem[] = [
  {
    id: 1,
    heading: 'Residential Foundations & Footings',
    description:
      'Provide your home with a rock-solid start using cement designed for maximum density.',
    media: {
      id: 1,
      url: '/images/building.png',
      alternativeText: 'Residential construction',
    },
  },
  {
    id: 2,
    heading: 'RCC Slabs, Beams, and Columns',
    description: 'Create the core skeletal strength required for any modern residential project.',
    media: {
      id: 2,
      url: '/images/product-ban.jpg',
      alternativeText: 'RCC construction',
    },
  },
  {
    id: 3,
    heading: 'Mass Concrete Works',
    description:
      'Deploy in large-scale pours like retaining walls, bridges, and basement structures.',
    media: {
      id: 3,
      url: '/images/sustain.png',
      alternativeText: 'Mass concrete works',
    },
  },
  {
    id: 4,
    heading: 'Hydraulic Structures',
    description:
      'Protect water tanks and sewage systems with our high-density, low-permeability formula.',
    media: {
      id: 4,
      url: '/images/main_banner.png',
      alternativeText: 'Hydraulic structures',
    },
  },
];

export default function ProductPpcApplications({
  heading = 'Wonder PPC Cement Uses: Essential Applications',
  subHeading = 'From high-load residential slabs to massive infrastructure in corrosive coastal environments, Wonder PPC is engineered for every critical stage of construction.',
  gridItems = defaultApplications,
}: ProductPpcApplicationsProps) {
  // Layout: distribute items across rows so each row is "balanced" per the
  // Figma reference. 4 items → 2 rows of 2. 5 items → row of 2 + row of 3.
  // 6 → 2 rows of 3. Other counts fall back to rows of 2 with a trailing
  // singleton handled by the same renderer.
  const groupedItems: ProductInnerGridItem[][] = [];
  const total = gridItems.length;
  if (total === 5) {
    groupedItems.push(gridItems.slice(0, 2));
    groupedItems.push(gridItems.slice(2, 5));
  } else if (total === 6) {
    groupedItems.push(gridItems.slice(0, 3));
    groupedItems.push(gridItems.slice(3, 6));
  } else {
    for (let i = 0; i < total; i += 2) {
      groupedItems.push(gridItems.slice(i, i + 2));
    }
  }

  return (
    <section className="bg-white pt-10 sm:pt-12 md:pt-14">
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="mx-auto mb-8 max-w-3xl text-center md:mb-10">
          <h2 className="text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl lg:text-[2.5rem] lg:leading-tight">
            {heading}
          </h2>
          {subHeading && (
            <div
              className="mt-4 text-base leading-relaxed text-gray-600 md:text-lg prose prose-gray mx-auto"
              dangerouslySetInnerHTML={{ __html: subHeading }}
            />
          )}
        </div>

        <div className="flex flex-col gap-5 md:gap-6">
          {groupedItems.map((row) => (
            <div
              key={row[0].id}
              className="flex flex-col gap-5 lg:flex-row lg:items-stretch lg:gap-6"
            >
              {row.map((item) => (
                <div
                  key={item.id}
                  className="group relative min-h-[280px] w-full min-w-0 flex-1 overflow-hidden rounded-lg bg-neutral-200 sm:min-h-[320px] lg:min-h-[350px]"
                >
                  {item.media ? (
                    <ImageWithFallback
                      image={item.media}
                      alt={item.media.alternativeText || item.heading || 'Application image'}
                      className="object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      fill
                      sizes={`(max-width: 1023px) 100vw, ${Math.round(100 / row.length)}vw`}
                    />
                  ) : (
                    <CmsImage
                      src={MEDIA_PLACEHOLDER}
                      alt={item.heading || 'Application image'}
                      fill
                      sizes={`(max-width: 768px) 100vw, ${Math.round(100 / row.length)}vw`}
                      className="object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      fallbackSrc={MEDIA_PLACEHOLDER}
                    />
                  )}
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/10"
                    aria-hidden
                  />
                  <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end p-5 sm:p-6 lg:p-8">
                    <h3 className="text-lg font-semibold text-white md:text-xl">{item.heading}</h3>
                    <div
                      className="prose prose-sm prose-invert mt-2 max-h-0 max-w-xl translate-y-2 overflow-hidden text-sm leading-relaxed text-white/90 opacity-0 transition-[max-height,opacity,transform] duration-500 group-hover:max-h-28 group-hover:translate-y-0 group-hover:opacity-100 md:text-base"
                      dangerouslySetInnerHTML={{ __html: item.description || '' }}
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-red-600 transition-all duration-700 group-hover:w-full" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
